import { TouchInput, type TouchDirection } from '../controls/touchInput';
import type { ControlMode } from '../controls/types';
import { createElement, getUiRoot } from './dom';
import { setLabel } from './i18n';
import { createIcon, type UiIcon } from './icons';

interface TouchControlState {
  readonly mode: ControlMode;
  readonly multiplayer: boolean;
  readonly blocked: boolean;
  readonly touring: boolean;
  readonly behind: boolean;
}

interface Callbacks {
  readonly menu: () => void;
  readonly view: () => void;
  readonly chat: () => void;
}

function describe(control: HTMLButtonElement, label: string, icon: UiIcon): void {
  setLabel(control, 'aria-label', label);
  setLabel(control, 'title', label);
  const image = createIcon(icon, 16);
  image.setAttribute('aria-hidden', 'true');
  image.setAttribute('stroke-width', '1.5');
  control.replaceChildren(image);
}

function button(
  parent: HTMLElement,
  label: string,
  icon: UiIcon,
  action?: () => void
): HTMLButtonElement {
  const element = createElement('button', '', parent);
  element.type = 'button';
  describe(element, label, icon);
  if (action) element.addEventListener('click', action);
  return element;
}

function movementButton(
  parent: HTMLElement,
  input: TouchInput,
  direction: TouchDirection,
  icon: UiIcon,
  label: string
): HTMLButtonElement {
  const control = button(parent, label, icon);
  control.dataset.direction = direction;
  setLabel(control, 'aria-label', label);
  setLabel(control, 'title', label);
  control.addEventListener('pointerdown', (event) => {
    if (event.button !== 0) return;
    event.preventDefault();
    control.setPointerCapture(event.pointerId);
    input.hold(event.pointerId, direction);
  });
  const release = (event: PointerEvent): void => {
    input.release(event.pointerId);
    if (control.hasPointerCapture(event.pointerId)) control.releasePointerCapture(event.pointerId);
  };
  control.addEventListener('pointerup', release);
  control.addEventListener('pointercancel', release);
  control.addEventListener('lostpointercapture', release);
  const keyboardId =
    -1 - ['forward', 'left', 'backward', 'right', 'ascend', 'descend'].indexOf(direction);
  for (const type of ['keydown', 'keyup'] as const)
    control.addEventListener(type, (event) => {
      if (event.code !== 'Space' && event.code !== 'Enter') return;
      event.preventDefault();
      event.stopPropagation();
      if (type === 'keydown') input.hold(keyboardId, direction);
      else input.release(keyboardId);
    });
  control.addEventListener('blur', () => {
    input.release(keyboardId);
  });
  return control;
}

function navigation(root: HTMLElement, input: TouchInput, callbacks: Callbacks) {
  const toolbar = createElement('div', 'touch-toolbar', root);
  button(toolbar, 'Menu', 'menu', callbacks.menu);
  const view = button(toolbar, 'Behind the visitor view', 'behind', callbacks.view);
  const chat = button(toolbar, 'Chat', 'chat', callbacks.chat);
  const travel = createElement('div', 'touch-travel', root);
  const flight = button(travel, 'Fly', 'fly', () => {
    input.perform('toggleMode');
  });
  const boat = button(travel, 'Board boat', 'boat', () => {
    input.perform('toggleBoat');
  });
  const painting = button(travel, 'Painting', 'painter', () => {
    input.perform('painterView');
  });
  return { view, chat, travel, flight, boat, painting };
}

function movement(root: HTMLElement, input: TouchInput) {
  const pad = createElement('div', 'touch-movement', root);
  pad.setAttribute('role', 'group');
  setLabel(pad, 'aria-label', 'Movement');
  const forward = movementButton(pad, input, 'forward', 'arrow-up', 'Move forward');
  const left = movementButton(pad, input, 'left', 'arrow-left', 'Move left');
  const backward = movementButton(pad, input, 'backward', 'arrow-down', 'Move backward');
  const right = movementButton(pad, input, 'right', 'arrow-right', 'Move right');
  const height = createElement('div', 'touch-height', root);
  const ascend = movementButton(height, input, 'ascend', 'ascend', 'Fly higher');
  const boost = button(height, 'Run', 'boost', () => {
    input.toggleBoost();
  });
  const descend = movementButton(height, input, 'descend', 'descend', 'Fly lower');
  return { pad, height, ascend, boost, descend, forward, left, backward, right };
}

function labels(
  nav: ReturnType<typeof navigation>,
  move: ReturnType<typeof movement>,
  state: TouchControlState
): void {
  describe(
    nav.flight,
    state.mode === 'fly' ? 'Walk' : 'Fly',
    state.mode === 'fly' ? 'walk' : 'fly'
  );
  describe(
    nav.boat,
    state.mode === 'boat' ? 'Step ashore' : 'Board boat',
    state.mode === 'boat' ? 'walk' : 'boat'
  );
  describe(
    move.boost,
    state.mode === 'fly' ? 'Boost' : state.mode === 'boat' ? 'Pull hard' : 'Run',
    'boost'
  );
  describe(
    nav.view,
    state.behind ? 'First-person view' : 'Behind the visitor view',
    state.behind ? 'first-person' : 'behind'
  );
  for (const direction of ['left', 'right'] as const) {
    const label = state.mode === 'boat' ? `Turn ${direction}` : `Move ${direction}`;
    setLabel(move[direction], 'aria-label', label);
    setLabel(move[direction], 'title', label);
  }
}

export function createTouchControls(callbacks: Callbacks) {
  const input = new TouchInput();
  const root = createElement('section', 'touch-controls', getUiRoot());
  setLabel(root, 'aria-label', 'Touch controls');
  const nav = navigation(root, input, callbacks);
  const move = movement(root, input);
  const directions = [
    move.forward,
    move.left,
    move.backward,
    move.right,
    move.ascend,
    move.descend,
  ];
  let previous = '';
  return {
    input,
    update(state: TouchControlState): void {
      root.hidden = state.blocked;
      move.pad.hidden = nav.travel.hidden = move.height.hidden = state.touring;
      move.ascend.hidden = move.descend.hidden = state.mode !== 'fly';
      nav.chat.hidden = !state.multiplayer;
      nav.flight.hidden = nav.painting.hidden = state.multiplayer;
      for (const control of [
        ...directions,
        nav.flight,
        nav.boat,
        nav.painting,
        move.boost,
        nav.view,
      ])
        control.disabled = state.mode === 'transition' || state.touring;
      for (const control of directions)
        control.dataset.held = String(input.isHeld(control.dataset.direction as TouchDirection));
      move.boost.setAttribute('aria-pressed', String(input.boosted));
      const signature = `${state.mode}:${state.behind}`;
      if (signature === previous) return;
      previous = signature;
      labels(nav, move, state);
    },
    dispose(): void {
      input.clear();
      root.remove();
    },
  };
}
